import type { StockItemCategory } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  RowActions,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeftRight, Boxes, PackageMinus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { quantityLabel } from '@/features/stock/labels';
import { MovementDialog } from '@/features/stock/MovementDialog';
import {
  stockItemsQueryOptions,
  stockLevelsQueryOptions,
  stockLocationsQueryOptions,
  stockSummaryQueryOptions,
} from '@/features/stock/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/stock/')({ component: StockLevelsPage });

const CATEGORIES: StockItemCategory[] = ['BEVERAGE', 'FOOD', 'SUPPLY', 'OTHER'];

/** Niveaux & alertes (Horevia Inventory, ADR-020) : quantités par lieu, seuils, valeur au coût moyen. */
function StockLevelsPage() {
  const { t, i18n } = useTranslation(['stock', 'common', 'errors']);
  const session = useSession();
  const { propertyId } = useCurrentProperty();
  const canRead = session.can('stock.read');
  const canMove = session.can('stock.move');
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [category, setCategory] = useState<StockItemCategory | undefined>(undefined);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    mode: 'MOVE' | 'TRANSFER';
    itemId?: string;
    locationId?: string;
  }>({ open: false, mode: 'MOVE' });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const filter = useMemo(
    () => ({ propertyId, category, locationId, alertsOnly, q: debounced || undefined }),
    [propertyId, category, locationId, alertsOnly, debounced],
  );
  const levels = useQuery(stockLevelsQueryOptions(filter, canRead));
  const summary = useQuery(stockSummaryQueryOptions(propertyId, canRead));
  const locations = useQuery(stockLocationsQueryOptions(propertyId, canRead));
  const items = useQuery(stockItemsQueryOptions({ propertyId }, canRead && canMove));
  const currency = summary.data?.currencyCode ?? session.user.tenant.currencyCode;

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('stock:levels.title')}
        subtitle={t('stock:levels.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('stock:title') },
          { label: t('stock:levels.title') },
        ]}
        actions={
          canMove && propertyId ? (
            <div className="row row--wrap" style={{ gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => setDialog({ open: true, mode: 'TRANSFER' })}
              >
                <ArrowLeftRight className="ico" aria-hidden="true" />
                {t('stock:levels.transfer')}
              </Button>
              <Button variant="primary" onClick={() => setDialog({ open: true, mode: 'MOVE' })}>
                <PackageMinus className="ico" aria-hidden="true" />
                {t('stock:levels.adjust')}
              </Button>
            </div>
          ) : null
        }
      />

      {!propertyId ? (
        <EmptyState
          icon={<Boxes className="ico" aria-hidden="true" />}
          title={t('stock:noProperty.title')}
          description={t('stock:noProperty.body')}
        />
      ) : (
        <>
          <div className="grid grid-4">
            <KpiCard
              label={t('stock:levels.kpi.items')}
              value={summary.data?.itemsCount ?? 0}
              tone="violet"
              loading={summary.isPending}
            />
            <KpiCard
              label={t('stock:levels.kpi.alerts')}
              value={summary.data?.alertsCount ?? 0}
              tone={summary.data && summary.data.alertsCount > 0 ? 'red' : 'green'}
              icon={<AlertTriangle className="ico" aria-hidden="true" />}
              loading={summary.isPending}
            />
            <KpiCard
              label={t('stock:levels.kpi.value')}
              value={formatAmount(summary.data?.value ?? 0, i18n.language, currency)}
              tone="green"
              loading={summary.isPending}
            />
            <KpiCard
              label={t('stock:levels.kpi.locations')}
              value={summary.data?.locationsCount ?? 0}
              tone="blue"
              loading={summary.isPending}
            />
          </div>

          <Card>
            <CardBody density="flush">
              <Toolbar>
                <div className="row row--wrap" style={{ gap: 8, flex: 1 }}>
                  <Input
                    aria-label={t('stock:levels.search')}
                    placeholder={t('stock:levels.search')}
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    style={{ maxWidth: 280 }}
                  />
                  <select
                    className="select"
                    aria-label={t('stock:levels.allCategories')}
                    value={category ?? ''}
                    onChange={(event) =>
                      setCategory(
                        (event.target.value || undefined) as StockItemCategory | undefined,
                      )
                    }
                  >
                    <option value="">{t('stock:levels.allCategories')}</option>
                    {CATEGORIES.map((value) => (
                      <option key={value} value={value}>
                        {t(`stock:category.${value}`)}
                      </option>
                    ))}
                  </select>
                  <select
                    className="select"
                    aria-label={t('stock:levels.allLocations')}
                    value={locationId ?? ''}
                    onChange={(event) => setLocationId(event.target.value || undefined)}
                  >
                    <option value="">{t('stock:levels.allLocations')}</option>
                    {(locations.data ?? []).map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  <ChipFilter active={alertsOnly} onClick={() => setAlertsOnly((value) => !value)}>
                    <AlertTriangle className="ico ico-16" aria-hidden="true" />
                    {t('stock:levels.alertsOnly')}
                  </ChipFilter>
                </div>
              </Toolbar>
              {levels.isPending ? (
                <TableSkeleton rows={6} columns={6} label={t('common:states.loading')} />
              ) : levels.isError ? (
                <ErrorState
                  title={t('common:states.errorTitle')}
                  description={errorMessage(levels.error, t)}
                  traceId={errorTraceId(levels.error)}
                  retryLabel={t('common:actions.retry')}
                  onRetry={() => void levels.refetch()}
                  traceCopyLabel={t('common:actions.copy')}
                  traceCopiedLabel={t('common:actions.copied')}
                />
              ) : levels.data.length === 0 ? (
                <EmptyState
                  icon={<Boxes className="ico" aria-hidden="true" />}
                  title={alertsOnly ? t('stock:levels.noAlert') : t('stock:levels.empty')}
                  description={alertsOnly ? undefined : t('stock:levels.emptyHint')}
                />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('stock:levels.columns.item')}</Th>
                        <Th>{t('stock:levels.columns.locations')}</Th>
                        <Th align="right">{t('stock:levels.columns.total')}</Th>
                        <Th align="right">{t('stock:levels.columns.min')}</Th>
                        <Th align="right">{t('stock:levels.columns.value')}</Th>
                        {canMove ? (
                          <Th align="right">{t('stock:levels.columns.actions')}</Th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {levels.data.map((entry) => (
                        <Tr key={entry.item.id}>
                          <Td>
                            <div className="stack" style={{ gap: 2 }}>
                              <strong>{entry.item.name}</strong>
                              <span className="t-12 muted">
                                {entry.item.code} · {t(`stock:category.${entry.item.category}`)} ·{' '}
                                {t(`stock:unit.${entry.item.unit}`)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="row row--wrap" style={{ gap: 6 }}>
                              {entry.levels.map((level) => (
                                <Badge
                                  key={level.locationId}
                                  tone={level.quantity < 0 ? 'red' : 'neutral'}
                                >
                                  {level.locationName} :{' '}
                                  {quantityLabel(level.quantity, i18n.language)}
                                </Badge>
                              ))}
                            </div>
                          </Td>
                          <Td numeric align="right">
                            <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              {entry.isAlert ? (
                                <Badge tone="red">
                                  {entry.total < 0
                                    ? t('stock:levels.negative')
                                    : t('stock:levels.alert')}
                                </Badge>
                              ) : null}
                              <strong>{quantityLabel(entry.total, i18n.language)}</strong>
                            </span>
                          </Td>
                          <Td numeric align="right">
                            {entry.item.minQuantity === null
                              ? '—'
                              : quantityLabel(entry.item.minQuantity, i18n.language)}
                          </Td>
                          <Td numeric align="right">
                            {formatAmount(entry.value, i18n.language, entry.item.currencyCode)}
                          </Td>
                          {canMove ? (
                            <Td align="right">
                              <RowActions>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setDialog({
                                      open: true,
                                      mode: 'MOVE',
                                      itemId: entry.item.id,
                                      locationId: entry.levels[0]?.locationId,
                                    })
                                  }
                                >
                                  {t('stock:levels.adjust')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setDialog({
                                      open: true,
                                      mode: 'TRANSFER',
                                      itemId: entry.item.id,
                                      locationId: entry.levels[0]?.locationId,
                                    })
                                  }
                                >
                                  {t('stock:levels.transfer')}
                                </Button>
                              </RowActions>
                            </Td>
                          ) : null}
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardBody>
          </Card>

          {canMove ? (
            <MovementDialog
              open={dialog.open}
              onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
              mode={dialog.mode}
              items={items.data ?? []}
              locations={locations.data ?? []}
              itemId={dialog.itemId}
              locationId={dialog.locationId}
            />
          ) : null}
        </>
      )}
    </Container>
  );
}
